import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Trash2, Plus, Minus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CartPage() {
  const navigate = useNavigate();
  const { user, fetchCartCount } = useContext(AuthContext);
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showGuestDialog, setShowGuestDialog] = useState(false);
  const [guestEmail, setGuestEmail] = useState('');

  useEffect(() => {
    const fetchCart = async () => {
      try {
        const response = await axios.get(`${API}/cart`, { withCredentials: true });
        setCartItems(response.data.items);
      } catch (error) {
        console.error('Error fetching cart:', error);
      }
    };

    const loadGuestCart = () => {
      const guestCart = JSON.parse(localStorage.getItem('guestCart') || '[]');
      setCartItems(guestCart);
    };

    if (user) {
      fetchCart();
    } else {
      loadGuestCart();
    }
  }, [user]);

  const refreshCart = async () => {
    if (user) {
      try {
        const response = await axios.get(`${API}/cart`, { withCredentials: true });
        setCartItems(response.data.items);
      } catch (error) {
        console.error('Error fetching cart:', error);
      }
    }
  };

  const updateQuantity = async (cartId, newQuantity) => {
    if (newQuantity < 1) return;
    
    if (user) {
      try {
        await axios.put(`${API}/cart/${cartId}`, { quantity: newQuantity }, { withCredentials: true });
        await refreshCart();
        await fetchCartCount();
      } catch (error) {
        toast.error('Failed to update quantity');
      }
    } else {
      // Guest cart - update localStorage
      const guestCart = JSON.parse(localStorage.getItem('guestCart') || '[]');
      const item = guestCart.find(i => i.cart_item.id === cartId);
      if (item) {
        item.cart_item.quantity = newQuantity;
        localStorage.setItem('guestCart', JSON.stringify(guestCart));
        setCartItems([...guestCart]);
        window.dispatchEvent(new Event('guestCartUpdated'));
      }
    }
  };

  const removeItem = async (cartId) => {
    if (user) {
      try {
        await axios.delete(`${API}/cart/${cartId}`, { withCredentials: true });
        await refreshCart();
        await fetchCartCount();
        toast.success('Item removed from cart');
      } catch (error) {
        toast.error('Failed to remove item');
      }
    } else {
      // Guest cart - remove from localStorage
      const guestCart = JSON.parse(localStorage.getItem('guestCart') || '[]');
      const updatedCart = guestCart.filter(i => i.cart_item.id !== cartId);
      localStorage.setItem('guestCart', JSON.stringify(updatedCart));
      setCartItems(updatedCart);
      window.dispatchEvent(new Event('guestCartUpdated'));
      toast.success('Item removed from cart');
    }
  };

  const checkout = async () => {
    if (!user) {
      setShowGuestDialog(true);
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/orders/checkout`, { origin_url: window.location.origin }, { withCredentials: true });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Checkout failed');
      setLoading(false);
    }
  };

  const guestCheckout = async () => {
    if (!guestEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/orders/guest-checkout`, {
        cart_items: cartItems.map(item => ({
          item_id: item.cart_item.item_id,
          item_type: item.cart_item.item_type,
          quantity: item.cart_item.quantity
        })),
        guest_email: guestEmail,
        origin_url: window.location.origin
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Checkout failed');
      setLoading(false);
    }
  };

  const total = cartItems.reduce((sum, item) => sum + (item.item_details?.price || 0) * item.cart_item.quantity, 0);

  return (
    <div className="min-h-screen" style={{ background: "#cc5500" }}>
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-5xl font-bold text-white mb-8" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="cart-title">
          Shopping Cart
        </h1>

        {cartItems.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-xl text-sage-600 mb-6">Your cart is empty</p>
            <Button onClick={() => navigate('/shop')} className="bg-sage-600 hover:bg-sage-700">
              Continue Shopping
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {cartItems.map(({ cart_item, item_details }) => (
              <div key={cart_item.id} className="bg-white rounded-xl p-6 shadow-md flex items-center gap-6" data-testid={`cart-item-${cart_item.id}`}>
                <img src={item_details?.image_url} alt={item_details?.name} className="w-24 h-24 object-cover rounded-lg" />
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-sage-900">{item_details?.name}</h3>
                  <p className="text-terracotta-600 font-bold mt-2">${item_details?.price?.toFixed(2)}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Button size="icon" variant="outline" onClick={() => updateQuantity(cart_item.id, cart_item.quantity - 1)}>
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="text-lg font-medium w-8 text-center" data-testid={`cart-quantity-${cart_item.id}`}>{cart_item.quantity}</span>
                  <Button size="icon" variant="outline" onClick={() => updateQuantity(cart_item.id, cart_item.quantity + 1)}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <Button size="icon" variant="ghost" onClick={() => removeItem(cart_item.id)} data-testid={`remove-item-${cart_item.id}`}>
                  <Trash2 className="w-5 h-5 text-red-500" />
                </Button>
              </div>
            ))}

            <div className="bg-sage-50 rounded-xl p-6 mt-8">
              <div className="flex justify-between items-center mb-6">
                <span className="text-2xl font-semibold text-sage-900">Total:</span>
                <span className="text-3xl font-bold text-terracotta-600" data-testid="cart-total">${total.toFixed(2)}</span>
              </div>
              
              {user ? (
                <Button
                  size="lg"
                  className="w-full bg-sage-600 hover:bg-sage-700 text-lg py-6 rounded-full"
                  onClick={checkout}
                  disabled={loading}
                  data-testid="checkout-button"
                >
                  {loading ? 'Processing...' : 'Proceed to Checkout'}
                </Button>
              ) : (
                <div className="space-y-3">
                  <Button
                    size="lg"
                    className="w-full bg-sage-600 hover:bg-sage-700 text-lg py-6 rounded-full"
                    onClick={() => navigate('/auth')}
                  >
                    Login to Checkout
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full text-lg py-6 rounded-full border-sage-600 text-sage-600 hover:bg-sage-50"
                    onClick={checkout}
                    disabled={loading}
                  >
                    Continue as Guest
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Guest Checkout Dialog */}
        <Dialog open={showGuestDialog} onOpenChange={setShowGuestDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Guest Checkout</DialogTitle>
              <DialogDescription>
                Enter your email to complete your purchase. You will receive order updates at this address.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="guest-email">Email Address</Label>
              <Input
                id="guest-email"
                type="email"
                placeholder="your@email.com"
                value={guestEmail}
                onChange={(e) => setGuestEmail(e.target.value)}
                className="mt-2"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowGuestDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={guestCheckout}
                disabled={loading}
                className="bg-sage-600 hover:bg-sage-700"
              >
                {loading ? 'Processing...' : 'Continue to Payment'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
